import { useState, useEffect } from 'react'
import { loadFromLocalStorage } from '@/lib/utils/local-storage'
import type { SecurityOptions, PasscodeOptions, FormatOptions, ShareFormat } from '@/types/share'

// localStorage keys for persisted defaults
const LS_KEY_SECURITY = 'myrkup:defaultSecurity'
const LS_KEY_PASSCODE = 'myrkup:defaultPasscode'
const LS_KEY_FORMAT = 'myrkup:defaultFormat'

export const DEFAULT_SECURITY_OPTIONS: SecurityOptions = {
  expiresIn: '1d',
  burnAfterRead: false,
  isModifiable: false,
}

export const DEFAULT_PASSCODE_OPTIONS: PasscodeOptions = {
  passcode: '',
  embedInUrl: true,
  useCustomPasscode: false,
  isPublic: false,
}

export const DEFAULT_FORMAT_OPTIONS: FormatOptions = {
  format: 'md' as ShareFormat,
}

/**
 * Manages persisted default share options (security, passcode, format).
 * Values are loaded from localStorage on mount and saved back on change.
 * These are the "global defaults" shown in the Settings modal.
 */
export function useDefaultShareOptions() {
  const [defaultSecurityOptions, setDefaultSecurityOptions] = useState<SecurityOptions>(
    () => loadFromLocalStorage(LS_KEY_SECURITY, DEFAULT_SECURITY_OPTIONS)
  )

  const [defaultPasscodeOptions, setDefaultPasscodeOptions] = useState<PasscodeOptions>(
    () => loadFromLocalStorage(LS_KEY_PASSCODE, DEFAULT_PASSCODE_OPTIONS)
  )

  const [defaultFormatOptions, setDefaultFormatOptions] = useState<FormatOptions>(
    () => loadFromLocalStorage(LS_KEY_FORMAT, DEFAULT_FORMAT_OPTIONS)
  )

  // Persist to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY_SECURITY, JSON.stringify(defaultSecurityOptions))
    } catch (e) {
      console.warn('Failed to save default security options', e)
    }
  }, [defaultSecurityOptions])

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY_PASSCODE, JSON.stringify(defaultPasscodeOptions))
    } catch (e) {
      console.warn('Failed to save default passcode options', e)
    }
  }, [defaultPasscodeOptions])

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY_FORMAT, JSON.stringify(defaultFormatOptions))
    } catch (e) {
      console.warn('Failed to save default format options', e)
    }
  }, [defaultFormatOptions])

  return {
    defaultSecurityOptions,
    setDefaultSecurityOptions,
    defaultPasscodeOptions,
    setDefaultPasscodeOptions,
    defaultFormatOptions,
    setDefaultFormatOptions,
  }
}
